"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { type Locale, isRTL, localePath } from "@/lib/i18n";
import { useSwitcherLocales } from "@/lib/useSwitcherLocales";
import { setManualLocale } from "@/lib/localePreference";
import {
    MapPin,
    ShieldCheck,
    Fuel,
    Truck,
    Building2,
    Bus,
    Wrench,
    ChevronDown,
    Check,
    Menu,
    X,
    Globe,
    Layers,
    Sparkles,
    ArrowRight,
    ArrowLeft,
} from "lucide-react";

type HeaderProps = {
    locale: Locale;
};

export default function Header({ locale }: HeaderProps) {
    const tHeader = useTranslations("header");
    const tBrand = useTranslations("brand");
    const tLang = useTranslations("lang");
    const tNav = useTranslations("nav");
    const tGeneral = useTranslations("general");

    const pathname = usePathname();
    const isRtl = isRTL(locale);
    const switcherLocales = useSwitcherLocales();

    const [isScrolled, setIsScrolled] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLangOpen, setIsLangOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
    const [mobileAccordion, setMobileAccordion] = useState<string | null>(null);

    const langRef = useRef<HTMLDivElement>(null);
    const navRef = useRef<HTMLDivElement>(null);
    const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const handleMouseEnter = (menuKey: string) => {
        if (dropdownTimeoutRef.current) {
            clearTimeout(dropdownTimeoutRef.current);
            dropdownTimeoutRef.current = null;
        }
        setActiveDropdown(menuKey);
    };

    const handleMouseLeave = () => {
        if (dropdownTimeoutRef.current) {
            clearTimeout(dropdownTimeoutRef.current);
        }
        dropdownTimeoutRef.current = setTimeout(() => {
            setActiveDropdown(null);
        }, 180);
    };

    useEffect(() => {
        return () => {
            if (dropdownTimeoutRef.current) {
                clearTimeout(dropdownTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };
        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Close popovers on click outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (langRef.current && !langRef.current.contains(e.target as Node)) {
                setIsLangOpen(false);
            }
            if (navRef.current && !navRef.current.contains(e.target as Node)) {
                setActiveDropdown(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Prevent body scroll when mobile menu is open
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [isMenuOpen]);

    // Close mobile menu on route change
    useEffect(() => {
        setIsMenuOpen(false);
        setActiveDropdown(null);
    }, [pathname]);

    const flagFor = (code: Locale): string => {
        switch (code) {
            case "fa":
                return "/assets/ir.png";
            case "ar":
                return "/assets/ar.png";
            case "en":
                return "/assets/us.png";
            case "de":
                return "/assets/de.png";
            case "tr":
                return "/assets/tr.png";
            default:
                return "/assets/us.png";
        }
    };

    const nativeLangNames: Record<Locale, string> = {
        en: "English",
        ar: "العربية",
        de: "Deutsch",
        tr: "Türkçe",
        fa: "فارسی",
    };

    const platformItems = [
        {
            label: tNav("submenu.platform.tracking"),
            desc: locale === "ar"
                ? "تتبع مباشر في الوقت الفعلي مع مناطق وسياج جغرافي ذكي"
                : locale === "de"
                ? "Echtzeit-Standort, Geschwindigkeiten und Geofencing-Zonen"
                : locale === "tr"
                ? "Gerçek zamanlı konum, hız ve akıllı sanal sınır yönetimi"
                : "Real-time location, route history & custom geofence alerts",
            href: localePath(locale, "platform/tracking"),
            icon: MapPin,
            badge: "Core",
        },
        {
            label: tNav("submenu.platform.safety"),
            desc: locale === "ar"
                ? "كشف الفرملة المفاجئة والسرعة الزائدة وسلوكيات القيادة"
                : locale === "de"
                ? "Erkennung von starkem Bremsen, Rasen und Fahrverhalten"
                : locale === "tr"
                ? "Sert frenleme, aşırı hız ve sürüş güvenliği analitiği"
                : "Harsh braking, speeding detection & safety scorecards",
            href: localePath(locale, "platform/safety"),
            icon: ShieldCheck,
            badge: "Safety",
        },
        {
            label: tNav("submenu.platform.fleetOps"),
            desc: locale === "ar"
                ? "مراقبة استهلاك الوقود وجداول الصيانة الدورية والتقارير"
                : locale === "de"
                ? "Kraftstoffüberwachung, Wartungspläne und Flottenanalysen"
                : locale === "tr"
                ? "Yakıt izleme, bakım takvimi ve operasyonel raporlama"
                : "Fuel telemetry, maintenance schedules & operating costs",
            href: localePath(locale, "platform/fleet-ops"),
            icon: Fuel,
            badge: "Telemetry",
        },
    ];

    const solutionItems = [
        {
            label: tNav("submenu.solutions.logistics"),
            desc: locale === "ar"
                ? "إدارة الشحن والنقل والإرسال لمسافات طويلة"
                : locale === "de"
                ? "Fernverkehr, Gütertransport und Liefertransparenz"
                : locale === "tr"
                ? "Uzun mesafe taşımacılık, lojistik ve sevkiyat takibi"
                : "Long-haul freight, dispatch and cargo visibility",
            href: localePath(locale, "solutions/logistics"),
            icon: Truck,
        },
        {
            label: tNav("submenu.solutions.government"),
            desc: locale === "ar"
                ? "أساطيل البلديات ومركبات الطوارئ والأصول العامة"
                : locale === "de"
                ? "Kommunale Flotten, Einsatzfahrzeuge und Behörden"
                : locale === "tr"
                ? "Belediye filoları, acil durum araçları ve kamu kurumları"
                : "Municipal fleets, emergency response & public assets",
            href: localePath(locale, "solutions/government"),
            icon: Building2,
        },
        {
            label: tNav("submenu.solutions.passengerTransport"),
            desc: locale === "ar"
                ? "حافلات نقل الموظفين والمدارس والنقل الجماعي"
                : locale === "de"
                ? "Busse, Shuttles, Mitarbeiter- und Schülertransport"
                : locale === "tr"
                ? "Servis araçları, otobüsler ve toplu yolcu taşımacılığı"
                : "Buses, shuttles, corporate & student transit",
            href: localePath(locale, "solutions/passenger-transport"),
            icon: Bus,
        },
        {
            label: tNav("submenu.solutions.fieldService"),
            desc: locale === "ar"
                ? "الآليات الثقيلة وصهاريج الوقود والعمليات الميدانية"
                : locale === "de"
                ? "Schwere Maschinen, Tankwagen und Außendiensteinsätze"
                : locale === "tr"
                ? "Ağır iş makineleri, akaryakıt tankerleri ve saha operasyonları"
                : "Heavy equipment, fuel tankers & remote site operations",
            href: localePath(locale, "solutions/field-service"),
            icon: Wrench,
        },
    ];

    const ArrowIcon = isRtl ? ArrowLeft : ArrowRight;

    return (
        <header
            className={`sticky top-0 z-50 w-full transition-all duration-200 ${
                isScrolled
                    ? "bg-white/95 backdrop-blur-md shadow-xs border-b border-gray-100"
                    : "bg-white border-b border-gray-100"
            }`}
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-6 xl:px-8">
                <div className="flex items-center justify-between h-20 gap-2 xl:gap-4">
                    {/* Brand Logo & Name */}
                    <div className="flex items-center gap-3 shrink-0">
                        <Link
                            href={localePath(locale)}
                            className="group flex items-center gap-3 focus:outline-hidden"
                            aria-label={tNav("home")}
                        >
                            <div className="relative w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center p-1.5 ring-1 ring-orange-100 group-hover:ring-brand-orange/40 transition-all duration-200 shadow-xs shrink-0">
                                <Image
                                    src="/logo.png"
                                    alt="Farjad System Pardaz"
                                    width={36}
                                    height={36}
                                    className="object-contain"
                                    priority
                                />
                            </div>
                            <div className="flex flex-col shrink-0">
                                <span className="font-bold text-base tracking-tight text-gray-900 group-hover:text-brand-orange transition-colors whitespace-nowrap">
                                    {locale === "ar" ? tBrand("name") : "Farjad System Pardaz"}
                                </span>
                                <span className="text-[11px] text-gray-500 font-medium tracking-wide whitespace-nowrap">
                                    {tHeader("subtitle") || "Fleet Tracking & Telematics"}
                                </span>
                            </div>
                        </Link>
                    </div>

                    {/* Desktop Navigation Menu */}
                    <nav
                        ref={navRef}
                        className="hidden lg:flex items-center gap-0.5 xl:gap-1.5 2xl:gap-2 whitespace-nowrap shrink-0"
                        dir={isRtl ? "rtl" : "ltr"}
                    >
                        {/* Platform Mega Menu */}
                        <div
                            className="relative py-2 shrink-0"
                            onMouseEnter={() => handleMouseEnter("platform")}
                            onMouseLeave={handleMouseLeave}
                        >
                            <button
                                type="button"
                                onClick={() =>
                                    setActiveDropdown(
                                        activeDropdown === "platform" ? null : "platform"
                                    )
                                }
                                className={`flex items-center gap-1 xl:gap-1.5 px-2 lg:px-2.5 xl:px-3.5 py-2 rounded-lg text-xs xl:text-sm font-medium whitespace-nowrap transition-colors ${
                                    activeDropdown === "platform"
                                        ? "text-brand-orange bg-orange-50/60"
                                        : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                                }`}
                                aria-expanded={activeDropdown === "platform"}
                            >
                                <span className="whitespace-nowrap">{tNav("platform")}</span>
                                <ChevronDown
                                    size={14}
                                    className={`text-gray-400 shrink-0 transition-transform duration-200 ${
                                        activeDropdown === "platform" ? "rotate-180 text-brand-orange" : ""
                                    }`}
                                />
                            </button>

                            {/* Dropdown Panel with continuous hover padding bridge */}
                            {activeDropdown === "platform" && (
                                <div
                                    className={`absolute top-full pt-1.5 w-[420px] z-50 animate-in fade-in slide-in-from-top-1 duration-150 ${
                                        isRtl ? "right-0 text-right" : "left-0 text-left"
                                    }`}
                                    onMouseEnter={() => handleMouseEnter("platform")}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <div className="bg-white rounded-2xl border border-gray-100 shadow-xl p-3">
                                        <div className="p-2 mb-1 border-b border-gray-50 flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Layers className="text-brand-orange" size={16} />
                                                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                                    {tNav("platform")}
                                                </span>
                                            </div>
                                            <Link
                                                href={localePath(locale, "platform")}
                                                className="text-xs font-semibold text-brand-orange hover:underline flex items-center gap-1"
                                                onClick={() => setActiveDropdown(null)}
                                            >
                                                <span>Overview</span>
                                                <ArrowIcon size={12} />
                                            </Link>
                                        </div>
                                        <div className="space-y-1">
                                            {platformItems.map((item) => {
                                                const Icon = item.icon;
                                                return (
                                                    <Link
                                                        key={item.href}
                                                        href={item.href}
                                                        onClick={() => setActiveDropdown(null)}
                                                        className="group flex items-start gap-3 p-2.5 rounded-xl hover:bg-orange-50/50 transition-colors"
                                                    >
                                                        <div className="mt-0.5 p-2 rounded-lg bg-gray-50 text-gray-700 group-hover:bg-brand-orange group-hover:text-white transition-colors shrink-0">
                                                            <Icon size={18} />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <div className="flex items-center gap-2">
                                                                <span className="text-sm font-semibold text-gray-900 group-hover:text-brand-orange transition-colors">
                                                                    {item.label}
                                                                </span>
                                                            </div>
                                                            <p className="text-xs text-gray-500 mt-0.5 leading-relaxed line-clamp-2">
                                                                {item.desc}
                                                            </p>
                                                        </div>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Solutions Mega Menu */}
                        <div
                            className="relative py-2 shrink-0"
                            onMouseEnter={() => handleMouseEnter("solutions")}
                            onMouseLeave={handleMouseLeave}
                        >
                            <button
                                type="button"
                                onClick={() =>
                                    setActiveDropdown(
                                        activeDropdown === "solutions" ? null : "solutions"
                                    )
                                }
                                className={`flex items-center gap-1 xl:gap-1.5 px-2 lg:px-2.5 xl:px-3.5 py-2 rounded-lg text-xs xl:text-sm font-medium whitespace-nowrap transition-colors ${
                                    activeDropdown === "solutions"
                                        ? "text-brand-orange bg-orange-50/60"
                                        : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                                }`}
                                aria-expanded={activeDropdown === "solutions"}
                            >
                                <span className="whitespace-nowrap">{tNav("solutions")}</span>
                                <ChevronDown
                                    size={14}
                                    className={`text-gray-400 shrink-0 transition-transform duration-200 ${
                                        activeDropdown === "solutions" ? "rotate-180 text-brand-orange" : ""
                                    }`}
                                />
                            </button>

                            {/* Dropdown Panel with continuous hover padding bridge */}
                            {activeDropdown === "solutions" && (
                                <div
                                    className={`absolute top-full pt-1.5 w-[460px] z-50 animate-in fade-in slide-in-from-top-1 duration-150 ${
                                        isRtl ? "right-0 text-right" : "left-0 text-left"
                                    }`}
                                    onMouseEnter={() => handleMouseEnter("solutions")}
                                    onMouseLeave={handleMouseLeave}
                                >
                                    <div className="bg-white rounded-2xl border border-gray-100 shadow-xl p-3">
                                        <div className="p-2 mb-1 border-b border-gray-50 flex items-center justify-between">
                                            <div className="flex items-center gap-2">
                                                <Sparkles className="text-brand-orange" size={16} />
                                                <span className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                                                    {tNav("solutions")}
                                                </span>
                                            </div>
                                            <Link
                                                href={localePath(locale, "solutions")}
                                                className="text-xs font-semibold text-brand-orange hover:underline flex items-center gap-1"
                                                onClick={() => setActiveDropdown(null)}
                                            >
                                                <span>All Solutions</span>
                                                <ArrowIcon size={12} />
                                            </Link>
                                        </div>
                                        <div className="grid grid-cols-2 gap-1">
                                            {solutionItems.map((item) => {
                                                const Icon = item.icon;
                                                return (
                                                    <Link
                                                        key={item.href}
                                                        href={item.href}
                                                        onClick={() => setActiveDropdown(null)}
                                                        className="group flex flex-col p-2.5 rounded-xl hover:bg-orange-50/50 transition-colors"
                                                    >
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <div className="p-1.5 rounded-lg bg-gray-50 text-gray-700 group-hover:bg-brand-orange group-hover:text-white transition-colors shrink-0">
                                                                <Icon size={15} />
                                                            </div>
                                                            <span className="text-sm font-semibold text-gray-900 group-hover:text-brand-orange transition-colors truncate">
                                                                {item.label}
                                                            </span>
                                                        </div>
                                                        <p className="text-[11px] text-gray-500 leading-relaxed line-clamp-2">
                                                            {item.desc}
                                                        </p>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Direct Navigation Links */}
                        <Link
                            href={localePath(locale, "white-label")}
                            className={`px-2 lg:px-2.5 xl:px-3.5 py-2 rounded-lg text-xs xl:text-sm font-medium whitespace-nowrap transition-colors shrink-0 ${
                                pathname.includes("/white-label")
                                    ? "text-brand-orange bg-orange-50/60 font-semibold"
                                    : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                            }`}
                        >
                            {tNav("whiteLabel")}
                        </Link>

                        <Link
                            href={localePath(locale, "custom-development")}
                            className={`px-2 lg:px-2.5 xl:px-3.5 py-2 rounded-lg text-xs xl:text-sm font-medium whitespace-nowrap transition-colors shrink-0 ${
                                pathname.includes("/custom-development")
                                    ? "text-brand-orange bg-orange-50/60 font-semibold"
                                    : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                            }`}
                        >
                            {tNav("customDevelopment")}
                        </Link>

                        <Link
                            href={localePath(locale, "knowledge-base")}
                            className={`px-2 lg:px-2.5 xl:px-3.5 py-2 rounded-lg text-xs xl:text-sm font-medium whitespace-nowrap transition-colors shrink-0 ${
                                pathname.includes("/knowledge-base")
                                    ? "text-brand-orange bg-orange-50/60 font-semibold"
                                    : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                            }`}
                        >
                            {tGeneral("knowledge-base.title") || "Knowledge Base"}
                        </Link>

                        <Link
                            href={localePath(locale, "about")}
                            className={`px-2 lg:px-2.5 xl:px-3.5 py-2 rounded-lg text-xs xl:text-sm font-medium whitespace-nowrap transition-colors shrink-0 ${
                                pathname.includes("/about")
                                    ? "text-brand-orange bg-orange-50/60 font-semibold"
                                    : "text-gray-700 hover:text-gray-900 hover:bg-gray-50"
                            }`}
                        >
                            {tNav("about")}
                        </Link>
                    </nav>

                    {/* Right Side Action Items */}
                    <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                        {/* Language Selector Dropdown */}
                        <div className="relative shrink-0" ref={langRef}>
                            <button
                                type="button"
                                onClick={() => setIsLangOpen(!isLangOpen)}
                                className="flex items-center gap-1.5 sm:gap-2 px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 text-xs font-medium transition-all focus:outline-hidden whitespace-nowrap"
                                aria-label="Select Language"
                                aria-haspopup="listbox"
                                aria-expanded={isLangOpen}
                            >
                                <span className="relative w-4 h-3 overflow-hidden rounded-xs shrink-0">
                                    <Image
                                        src={flagFor(locale)}
                                        alt={tLang(locale)}
                                        fill
                                        sizes="16px"
                                        className="object-cover"
                                    />
                                </span>
                                <span className="hidden sm:inline-block whitespace-nowrap">
                                    {nativeLangNames[locale] || tLang(locale)}
                                </span>
                                <span className="sm:hidden uppercase font-semibold">
                                    {locale}
                                </span>
                                <ChevronDown size={13} className="text-gray-400 shrink-0" />
                            </button>

                            {isLangOpen && (
                                <div
                                    className={`absolute top-full mt-2 w-48 bg-white rounded-xl border border-gray-100 shadow-xl py-1.5 z-50 animate-in fade-in slide-in-from-top-1 duration-150 ${
                                        isRtl ? "left-0 text-right" : "right-0 text-left"
                                    }`}
                                >
                                    <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 border-b border-gray-50">
                                        Language / Sprache
                                    </div>
                                    <ul role="listbox" className="pt-1">
                                        {switcherLocales.map((code) => {
                                            const isCurrent = code === locale;
                                            return (
                                                <li key={code}>
                                                    <Link
                                                        href={localePath(code)}
                                                        onClick={() => {
                                                             setManualLocale(code);
                                                            setIsLangOpen(false);
                                                        }}
                                                        className={`flex items-center justify-between px-3 py-2 text-xs font-medium transition-colors ${
                                                            isCurrent
                                                                ? "bg-orange-50 text-brand-orange font-semibold"
                                                                : "text-gray-700 hover:bg-gray-50"
                                                        }`}
                                                    >
                                                        <div className="flex items-center gap-2.5">
                                                            <span className="relative w-4 h-3 overflow-hidden rounded-xs shrink-0">
                                                                <Image
                                                                    src={flagFor(code)}
                                                                    alt={tLang(code)}
                                                                    fill
                                                                    sizes="16px"
                                                                    className="object-cover"
                                                                />
                                                            </span>
                                                            <span className="whitespace-nowrap">
                                                                {nativeLangNames[code] || tLang(code)}
                                                            </span>
                                                        </div>
                                                        {isCurrent && (
                                                            <Check size={14} className="text-brand-orange shrink-0" />
                                                        )}
                                                    </Link>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Primary CTA (Request Demo / Contact) */}
                        <Link
                            href={localePath(locale, "contactus")}
                            className="inline-flex items-center justify-center whitespace-nowrap shrink-0 rounded-[10px] bg-[var(--sm-ink)] px-3.5 sm:px-4 py-2 text-xs sm:text-sm font-semibold !text-white shadow-[0_6px_16px_rgba(15,23,42,.18)] transition hover:-translate-y-0.5"
                        >
                            {tNav("contact")}
                        </Link>

                        {/* Mobile Menu Button */}
                        <button
                            type="button"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                            className="lg:hidden p-2 rounded-lg text-gray-700 hover:text-gray-900 hover:bg-gray-100 transition-colors focus:outline-hidden"
                            aria-label={tNav("toggleMenu")}
                            aria-expanded={isMenuOpen}
                        >
                            {isMenuOpen ? <X size={22} /> : <Menu size={22} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Drawer Menu */}
            {isMenuOpen && (
                <div
                    className="lg:hidden fixed inset-x-0 top-20 bottom-0 bg-white z-40 overflow-y-auto border-t border-gray-100 p-4 animate-in fade-in duration-150"
                    dir={isRtl ? "rtl" : "ltr"}
                >
                    <div className="flex flex-col gap-4 pb-12">
                        {/* Direct Home Link */}
                        <Link
                            href={localePath(locale)}
                            onClick={() => setIsMenuOpen(false)}
                            className="flex items-center justify-between p-3 rounded-xl bg-gray-50 font-semibold text-gray-900 text-sm"
                        >
                            <span>{tNav("home")}</span>
                            <ArrowIcon size={16} className="text-gray-400" />
                        </Link>

                        {/* Platform Accordion */}
                        <div className="border border-gray-100 rounded-xl overflow-hidden">
                            <button
                                type="button"
                                onClick={() =>
                                    setMobileAccordion(
                                        mobileAccordion === "platform" ? null : "platform"
                                    )
                                }
                                className="w-full flex items-center justify-between p-3.5 bg-gray-50/50 text-sm font-semibold text-gray-900 text-left"
                            >
                                <div className="flex items-center gap-2">
                                    <Layers size={16} className="text-brand-orange" />
                                    <span>{tNav("platform")}</span>
                                </div>
                                <ChevronDown
                                    size={16}
                                    className={`text-gray-500 transition-transform ${
                                        mobileAccordion === "platform" ? "rotate-180" : ""
                                    }`}
                                />
                            </button>
                            {mobileAccordion === "platform" && (
                                <div className="p-2 space-y-1 bg-white border-t border-gray-100">
                                    <Link
                                        href={localePath(locale, "platform")}
                                        onClick={() => setIsMenuOpen(false)}
                                        className="block p-2.5 rounded-lg text-xs font-bold text-brand-orange hover:bg-orange-50"
                                    >
                                        Overview →
                                    </Link>
                                    {platformItems.map((item) => {
                                        const Icon = item.icon;
                                        return (
                                            <Link
                                                key={item.href}
                                                href={item.href}
                                                onClick={() => setIsMenuOpen(false)}
                                                className="flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-gray-50 text-gray-800"
                                            >
                                                <Icon size={16} className="text-brand-orange mt-0.5 shrink-0" />
                                                <div>
                                                    <div className="text-xs font-semibold">{item.label}</div>
                                                    <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-1">
                                                        {item.desc}
                                                    </div>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Solutions Accordion */}
                        <div className="border border-gray-100 rounded-xl overflow-hidden">
                            <button
                                type="button"
                                onClick={() =>
                                    setMobileAccordion(
                                        mobileAccordion === "solutions" ? null : "solutions"
                                    )
                                }
                                className="w-full flex items-center justify-between p-3.5 bg-gray-50/50 text-sm font-semibold text-gray-900 text-left"
                            >
                                <div className="flex items-center gap-2">
                                    <Sparkles size={16} className="text-brand-orange" />
                                    <span>{tNav("solutions")}</span>
                                </div>
                                <ChevronDown
                                    size={16}
                                    className={`text-gray-500 transition-transform ${
                                        mobileAccordion === "solutions" ? "rotate-180" : ""
                                    }`}
                                />
                            </button>
                            {mobileAccordion === "solutions" && (
                                <div className="p-2 space-y-1 bg-white border-t border-gray-100">
                                    <Link
                                        href={localePath(locale, "solutions")}
                                        onClick={() => setIsMenuOpen(false)}
                                        className="block p-2.5 rounded-lg text-xs font-bold text-brand-orange hover:bg-orange-50"
                                    >
                                        All Solutions →
                                    </Link>
                                    {solutionItems.map((item) => {
                                        const Icon = item.icon;
                                        return (
                                            <Link
                                                key={item.href}
                                                href={item.href}
                                                onClick={() => setIsMenuOpen(false)}
                                                className="flex items-start gap-2.5 p-2.5 rounded-lg hover:bg-gray-50 text-gray-800"
                                            >
                                                <Icon size={16} className="text-brand-orange mt-0.5 shrink-0" />
                                                <div>
                                                    <div className="text-xs font-semibold">{item.label}</div>
                                                    <div className="text-[11px] text-gray-500 mt-0.5 line-clamp-1">
                                                        {item.desc}
                                                    </div>
                                                </div>
                                            </Link>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Direct Links */}
                        <div className="space-y-1 border border-gray-100 rounded-xl p-2 bg-white">
                            <Link
                                href={localePath(locale, "white-label")}
                                onClick={() => setIsMenuOpen(false)}
                                className="flex items-center justify-between p-2.5 rounded-lg text-xs font-semibold text-gray-800 hover:bg-gray-50"
                            >
                                <span>{tNav("whiteLabel")}</span>
                                <ArrowIcon size={14} className="text-gray-400" />
                            </Link>
                            <Link
                                href={localePath(locale, "custom-development")}
                                onClick={() => setIsMenuOpen(false)}
                                className="flex items-center justify-between p-2.5 rounded-lg text-xs font-semibold text-gray-800 hover:bg-gray-50"
                            >
                                <span>{tNav("customDevelopment")}</span>
                                <ArrowIcon size={14} className="text-gray-400" />
                            </Link>
                            <Link
                                href={localePath(locale, "knowledge-base")}
                                onClick={() => setIsMenuOpen(false)}
                                className="flex items-center justify-between p-2.5 rounded-lg text-xs font-semibold text-gray-800 hover:bg-gray-50"
                            >
                                <span>{tGeneral("knowledge-base.title") || "Knowledge Base"}</span>
                                <ArrowIcon size={14} className="text-gray-400" />
                            </Link>
                            <Link
                                href={localePath(locale, "about")}
                                onClick={() => setIsMenuOpen(false)}
                                className="flex items-center justify-between p-2.5 rounded-lg text-xs font-semibold text-gray-800 hover:bg-gray-50"
                            >
                                <span>{tNav("about")}</span>
                                <ArrowIcon size={14} className="text-gray-400" />
                            </Link>
                        </div>

                        {/* Mobile Language Switcher Grid */}
                        <div className="p-3 border border-gray-100 rounded-xl bg-gray-50/50">
                            <div className="text-xs font-semibold text-gray-500 mb-2 flex items-center gap-1.5">
                                <Globe size={14} />
                                <span>Language</span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                {switcherLocales.map((code) => {
                                    const isCurrent = code === locale;
                                    return (
                                        <Link
                                            key={code}
                                            href={localePath(code)}
                                            onClick={() => {
                                                setManualLocale(code);
                                                setIsMenuOpen(false);
                                            }}
                                            className={`flex items-center gap-2 p-2 rounded-lg text-xs font-medium transition-colors ${
                                                isCurrent
                                                    ? "bg-brand-orange text-white font-semibold"
                                                    : "bg-white text-gray-800 border border-gray-200/60"
                                            }`}
                                        >
                                            <span className="relative w-4 h-3 overflow-hidden rounded-xs shrink-0">
                                                <Image
                                                    src={flagFor(code)}
                                                    alt={tLang(code)}
                                                    fill
                                                    sizes="16px"
                                                    className="object-cover"
                                                />
                                            </span>
                                            <span className="truncate">
                                                {nativeLangNames[code] || tLang(code)}
                                            </span>
                                        </Link>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Mobile Actions */}
                        <div className="pt-2">
                            <Link
                                href={localePath(locale, "contactus")}
                                onClick={() => setIsMenuOpen(false)}
                                className="block w-full py-3 text-center text-sm font-semibold rounded-xl bg-[var(--sm-ink)] !text-white shadow-[0_6px_16px_rgba(15,23,42,.18)] transition hover:-translate-y-0.5"
                            >
                                {tNav("contact")}
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </header>
    );
}
